// The cascade's layers named before any module brings a stylesheet: Caliber's resets beneath the kit's (layers.css).
import './layers.css';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { lazyNamed } from './lib/lazyPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initTelemetry } from './lib/telemetry';
import { reloadForNewBuild } from './lib/staleBuild';
import { fetchParadigmConfig } from './lib/api/config';
import { bootstrapProductLimits } from './lib/productLimits';
import { loadSession, installSessionWriteWatch } from './lib/session';
import { ensureStorageVersion, currentUserId } from './lib/storage';
import { baseAddress, baseFor, ownUsername } from './lib/accountBase';
import { DEV_LOGIN_ENABLED, SPOOF_USER_ID, enterSpoof } from './lib/devSession';
// The shell's foundations (tokens, type, reset, the field), first. <Shell>
// imports the same module, so it loads once; the pages outside <Shell> (the
// landing, the long reads, the public pages) have no other way to them.
import '@ofself/shell/styles.css';
// The kit's instrument tokens (--ofself-sheet-*: the plate, its steps, the steering ground). The column, the pages and
// the instrument blocks wear them (app07.css, skillInstrument.css), but shell 0.356.0 bundles Instrument.css only with
// its Instrument parts, which Caliber does not import, so those grounds read transparent: a raised page, the quiz's
// sitting. The package exports no path to the sheet, so it is named by its file; drop this once styles.css carries it.
import '../node_modules/@ofself/shell/dist/anatomy/templates/Instrument.css';
// Hanken Grotesk — the shell's own --ofself-font-sans (Typography.css declares
// it but doesn't ship the font FILES; every ofself app self-hosts it).
import '@fontsource-variable/hanken-grotesk';
import './index.css';
// Loads after the kit + index.css so the app-level shell seam (gold accent,
// panel surface) wins ties.
import './shell-app.css';
// a prototype to look at, off unless asked for (?float=1): the rail and the side list afloat on the ether
import './prototypes/floatingChrome';
// The credential design-system tokens (root scope) — every credential
// surface consumes these; nothing re-declares them. See the file header.
import './styles/credential.css';
// A card that wears its own background re-roots those tokens on its inks
// (the backgrounds, 2026-09-22) — a scoped skin, so it loads after them.
import './styles/card-ground.css';
// The shared motion beats (landIn / stampIn / keptLand / readBar) — global
// keyframes + classes any surface can call; reduced motion stills them.
import './styles/motion.css';

const App = lazyNamed(() => import('./App'), 'App');
const AssessmentLab = lazyNamed(() => import('./pages/AssessmentLab'), 'AssessmentLab');
const AdminEntry = lazyNamed(() => import('./pages/AdminEntry'), 'AdminEntry');

// Reconcile persisted localStorage against the current STORAGE_VERSION BEFORE
// anything reads it, so a deploy that changes saved shapes self-heals and no
// user (or dev) has to manually clear site data to see their change.
const labEntry = /^\/(lab|assessment-lab)(\/|$)/.test(window.location.pathname);
const adminEntry = /^\/enter\/?$/.test(window.location.pathname);
if (!labEntry && !adminEntry) ensureStorageVersion();

// YOUR PAGES LIVE AT /@<username>/… (lib/accountBase): a bare app address —
// an old link, an email — moves under your base BEFORE anything boots, so the
// hop costs one document load and no session or config read twice. It waits on
// nothing, the dev seed included: the move is part of this document's load.
const moveTo = baseAddress(baseFor(ownUsername()), window.location);

// Everything from here waits on the dev seed where there is one (start, below).
function start() {
  // ERRORS, FIRST: an uncaught error or an unhandled rejection from here on is
  // posted to /api/client-error and lands in CloudWatch (lib/telemetry.ts) —
  // a crash on the way up included. It needs no config.
  initTelemetry();
  // CODE A DEPLOY HAS REPLACED: Vite says a page's code or stylesheet could not
  // be fetched; the page reloads for the new build, once (lib/staleBuild).
  // lazyNamed (lib/lazyPage) catches the same for every lazy page.
  window.addEventListener('vite:preloadError', (event) => { if (reloadForNewBuild()) event.preventDefault(); });
  // THE SESSION, ONCE. A signed-in user's page reads (config, profile,
  // onboarding status, verdicts, wallet, saved roles, job spine) arrive in one
  // request and prime lib/session.ts; every wrapper below reads from that until
  // a write or a change event invalidates its part. Writes are watched at the
  // fetch layer so no write path can forget to invalidate.
  installSessionWriteWatch();
  // SPOOF MODE: sign in as the seeded persona (a fresh cookie every boot), then
  // boot as them — reloading first when this changed who the page names.
  function boot() {
    const uid = currentUserId();
    if (uid) void loadSession(uid);
    // The session already includes config. Start it first so this reader joins
    // the bootstrap instead of issuing a second request on every page load.
    bootstrapProductLimits(fetchParadigmConfig());
    render();
  }
  if (SPOOF_USER_ID) void enterSpoof().then((changed) => (changed ? window.location.reload() : boot()));

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Root element #root not found in index.html');

  function render() {
    createRoot(rootEl!).render(
      <StrictMode>
        <ErrorBoundary>
          <Suspense fallback={null}><App /></Suspense>
        </ErrorBoundary>
      </StrictMode>,
    );
  }
  if (!SPOOF_USER_ID) boot();
}

// DEV ONLY (localhost): when the placeholder dev session is active, seeded fixtures answer known /api reads so the
// dev app renders populated instead of 403-empty; real sessions are never intercepted. Installed before anything
// starts, so no first-mount fetch can race past it; loaded only here, so its fixtures are a chunk production never
// fetches (they were 50 KB of every visitor's first script).
if (adminEntry) {
  initTelemetry();
  createRoot(document.getElementById('root')!).render(
    <StrictMode><ErrorBoundary><Suspense fallback={null}><AdminEntry /></Suspense></ErrorBoundary></StrictMode>,
  );
}
else if (labEntry) {
  if (window.location.pathname !== '/lab') window.location.replace(`/lab${window.location.search}`);
  else {
    initTelemetry();
    createRoot(document.getElementById('root')!).render(
      <StrictMode><ErrorBoundary><Suspense fallback={null}><AssessmentLab /></Suspense></ErrorBoundary></StrictMode>,
    );
  }
}
else if (moveTo) window.location.replace(moveTo);
else if (DEV_LOGIN_ENABLED) void import('./lib/devSeed').then((m) => m.installDevSeed(), () => undefined).then(start);
else start();
